import React, { useState, useEffect, useRef, useCallback } from "react";
import "../styles/FullscreenPrices.css";
import SmallBox, { sarrafiyeValues } from "../components/SmallBox";

interface Price {
  code: string;
  bid: number;
  ask: number;
  category: string;
  source: string;
}

const ITEMS_PER_PAGE = 15;

const FullscreenPrices: React.FC = () => {
  const [prices, setPrices] = useState<Price[]>([]);
  const [highlightedRows, setHighlightedRows] = useState<{ [code: string]: "increase" | "decrease" | "no-change" }>({});
  const socketRef = useRef<WebSocket | null>(null);

  const connectWebSocket = useCallback(() => {
    const wsUrl = "wss://sslsocket.aifanet.com/5.250.255.84/12669";

    socketRef.current = new WebSocket(wsUrl);

    socketRef.current.onopen = () => {
      console.log("WebSocket bağlantısı kuruldu.");
    };

    socketRef.current.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (Array.isArray(data)) {
          const updatedPrices = data.map((item: any) => ({
            code: item.Code.replace(/^E\./, 'ESKİ '),
            bid: item.Bid,
            ask: item.Ask,
            category: item.Category,
            source: "WebSocket",
          }));
          setPrices((prevPrices) => {
            const prevPriceMap = prevPrices.reduce((acc, price) => {
              acc[price.code] = price;
              return acc;
            }, {} as { [code: string]: Price });

            const newHighlightedRows: { [code: string]: "increase" | "decrease" | "no-change" } = {};
            updatedPrices.forEach((price) => {
              const prevBid = prevPriceMap[price.code]?.bid || 0;
              const prevAsk = prevPriceMap[price.code]?.ask || 0;

              if (price.bid !== prevBid) {
                newHighlightedRows[price.code] = price.bid > prevBid ? "increase" : "decrease";
              } else if (price.ask !== prevAsk) {
                newHighlightedRows[price.code] = price.ask > prevAsk ? "increase" : "decrease";
              } else {
                newHighlightedRows[price.code] = "no-change";
              }
            });

            setHighlightedRows(newHighlightedRows);
            return updatedPrices;
          });
        }
      } catch (error) {
        console.error("WebSocket mesaj hatası:", error);
      }
    };

    socketRef.current.onclose = () => {
      console.warn("WebSocket bağlantısı kapandı. Yeniden bağlanıyor...");
      setTimeout(connectWebSocket, 5000);
    };

    socketRef.current.onerror = (error) => {
      console.error("WebSocket hata:", error);
    };
  }, []);

  useEffect(() => {
    connectWebSocket();
    return () => {
      if (socketRef.current) {
        socketRef.current.close();
      }
    };
  }, [connectWebSocket]);

  const piyasaPrices = prices.filter((price) => price.category === "PIYASA FIYATLARI");
  const sarrafiyePrices = prices.filter((price) => price.category === "SARRAFIYE");
  const gramAltinPrices = prices.filter((price) => price.category === "GRAM ALTIN");
  const gumusPrices = prices.filter((price) => price.category === "GUMUS");

  const formatPrice = (price: number, code: string): string => {
    if (code === "EUR/USD") {
      return price.toLocaleString("tr-TR", { minimumFractionDigits: 4, maximumFractionDigits: 4 });
    } else if (["USD/KG", "EUR/KG", "GÜM/TL","GÜM/USD","GÜM/EUR", "USD/TL", "EUR/TL"].includes(code)) {
      return price.toLocaleString("tr-TR", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
    } else if (code.includes("GRAM") || code.includes("SARRAFIYE")) {
      return price.toLocaleString("tr-TR", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    } else {
      return price.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
  };

  const renderTable = (title: string, prices: Price[]) => (
    <div className="prices-table">
      <div className="table-header">
        <h2>{title}</h2>
      </div>
      <div className="scrollable-table">
        <table>
          <thead>
            <tr>
              <th>Birim</th>
              <th>Alış</th>
              <th>Satış</th>
            </tr>
          </thead>
          <tbody>
            {prices.slice(0, ITEMS_PER_PAGE).map((price) => (
              <tr key={price.code}>
                <td>{price.code}</td>
                <td
                  className={
                    highlightedRows[price.code] === "increase"
                      ? "increase"
                      : highlightedRows[price.code] === "decrease"
                      ? "decrease"
                      : "no-change"
                  }
                >
                  {price.category === "SARRAFIYE" && sarrafiyeValues[price.code] && (
                    <SmallBox label={price.code} type="bid" />
                  )}
                  {price.category === "GRAM ALTIN" && (
                    <SmallBox label={price.code} type="bid" />
                  )}
                  {(price.category === "SARRAFIYE" || price.category === "GRAM ALTIN" || price.category === "GUMUS")
                    ? Math.floor(price.bid).toLocaleString("tr-TR")
                    : price.bid.toFixed(2)} {price.category === "SARRAFIYE" && "₺"}
                </td>
                <td
                  className={
                    highlightedRows[price.code] === "increase"
                      ? "increase"
                      : highlightedRows[price.code] === "decrease"
                      ? "decrease"
                      : "no-change"
                  }
                >
                  {price.category === "SARRAFIYE" && sarrafiyeValues[price.code] && (
                    <SmallBox label={price.code} type="ask" />
                  )}
                  {price.category === "GRAM ALTIN" && (
                    <SmallBox label={price.code} type="ask" />
                  )}
                  {(price.category === "SARRAFIYE" || price.category === "GRAM ALTIN" || price.category === "GUMUS")
                    ? Math.floor(price.ask).toLocaleString("tr-TR")
                    : price.ask.toFixed(2)} {price.category === "SARRAFIYE" && "₺"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="fullscreen-prices-container">
      {renderTable("Piyasa Fiyatları", piyasaPrices)}
      {renderTable("Sarrafiye", sarrafiyePrices)}
      {renderTable("Gram Altın", gramAltinPrices)}
      {renderTable("Gümüş", gumusPrices)}
    </div>
  );
};

export default FullscreenPrices; 